import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  CircularProgress,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Divider,
  Paper,
  Avatar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Science as ScienceIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSlateLib,
  selectSlateLibData,
  selectSlateLibStatus,
  selectSlateLibError,
} from "../../../redux/slateLibSlice";

const SlateSelectorDialog = ({
  open,
  onClose,
  onSelect,
  multiSelect = false,
}) => {
  const dispatch = useDispatch();
  const slatesFromStore = useSelector(selectSlateLibData);
  const slatesStatus = useSelector(selectSlateLibStatus);
  const slatesError = useSelector(selectSlateLibError);

  const [search, setSearch] = useState("");
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const searchInputRef = useRef(null);

  // Normalize - Updated for SearchSlatesDto structure
  const getCOC = (s) => s?.coc ?? "";
  const getSampleBottle = (s) => s?.sampleBottle ?? "";
  const getPreservation = (s) => s?.preservation ?? "";
  const getJobId = (s) => s?.jobId ?? "";
  const getJobName = (s) => s?.jobName ?? "";

  // Load data
  useEffect(() => {
    if (open && slatesStatus === "idle") {
      dispatch(fetchSlateLib());
    }
  }, [open, slatesStatus, dispatch]);

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedJobIds([]);
      setSearch("");
    }
  }, [open]);

  // Filter + limit to 20
  const filteredSlates = useMemo(() => {
    let list = slatesFromStore || [];
    if (search.trim()) {
      const term = search.toLowerCase();
      list = list.filter((s) => {
        const coc = s?.coc?.toLowerCase() || "";
        const sampleBottle = s?.sampleBottle?.toLowerCase() || "";
        const preservation = s?.preservation?.toLowerCase() || "";
        const jobId = s?.jobId?.toLowerCase() || "";
        const jobName = s?.jobName?.toLowerCase() || "";

        return (
          coc.includes(term) ||
          sampleBottle.includes(term) ||
          preservation.includes(term) ||
          jobId.includes(term) ||
          jobName.includes(term)
        );
      });
    }
    return list.slice(0, 20);
  }, [slatesFromStore, search]);

  const toggleSelection = (jobId) => {
    if (!jobId) return; // Skip if jobId is empty/null

    if (!multiSelect) {
      setSelectedJobIds([jobId]);
      const slate = filteredSlates.find((s) => getJobId(s) === jobId);
      onSelect(slate || null);
      onClose();
      return;
    }
    setSelectedJobIds((prev) =>
      prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
    );
  };

  const handleConfirm = () => {
    if (multiSelect) {
      const selectedSlates = (slatesFromStore || []).filter((s) =>
        selectedJobIds.includes(getJobId(s))
      );
      onSelect(selectedSlates);
    } else if (selectedJobIds.length > 0) {
      const slate = (slatesFromStore || []).find(
        (s) => getJobId(s) === selectedJobIds[0]
      );
      onSelect(slate || null);
    } else {
      onSelect(null);
    }
    onClose();
  };

  const removeSelectedJobId = (jobIdToRemove) => {
    setSelectedJobIds((prev) => prev.filter((id) => id !== jobIdToRemove));
  };

  const getSlateDisplayText = (jobId) => {
    const slate = (slatesFromStore || []).find((s) => getJobId(s) === jobId);
    if (!slate) return jobId;
    const coc = getCOC(slate);
    const sampleBottle = getSampleBottle(slate);
    return coc || sampleBottle || jobId;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionProps={{
        onEntered: () => {
          searchInputRef.current?.focus();
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "60vh",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <ScienceIcon />
        {multiSelect ? "Select Slates" : "Select Slate"}
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {slatesError && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              bgcolor: "error.lighter",
              border: "1px solid",
              borderColor: "error.light",
              borderRadius: 2,
            }}
          >
            <Typography color="error" variant="body2">
              {slatesError}
            </Typography>
          </Paper>
        )}

        <TextField
          value={search}
          inputRef={searchInputRef}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by COC, sample bottle, or preservation..."
          fullWidth
          variant="outlined"
          size="small"
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: "grey.50",
              height: "40px",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Selected items chips for multiSelect */}
        {multiSelect && selectedJobIds.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Selected ({selectedJobIds.length}):
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {selectedJobIds.map((jobId) => {
                const displayText = getSlateDisplayText(jobId);
                return (
                  <Chip
                    key={jobId}
                    label={displayText}
                    onDelete={() => removeSelectedJobId(jobId)}
                    deleteIcon={<CloseIcon />}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                );
              })}
            </Box>
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        {slatesStatus === "loading" ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                Loading slates...
              </Typography>
            </Box>
          </Box>
        ) : filteredSlates.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              color: "text.secondary",
            }}
          >
            <SearchIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6">No results found</Typography>
            <Typography variant="body2">
              Try adjusting your search terms
            </Typography>
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              maxHeight: 400,
              overflowY: "auto",
            }}
          >
            <List dense sx={{ p: 0 }}>
              {filteredSlates.map((s, index) => {
                const jobId = getJobId(s);
                const coc = getCOC(s);
                const sampleBottle = getSampleBottle(s);
                const preservation = getPreservation(s);
                const jobName = getJobName(s);
                const isSelected = selectedJobIds.includes(jobId);
                const displayCOC = coc || "(No COC)";

                // Skip items without jobId
                if (!jobId) return null;

                return (
                  <React.Fragment key={jobId}>
                    <ListItem
                      disablePadding
                      sx={{
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <ListItemButton
                        onClick={() => toggleSelection(jobId)}
                        sx={{
                          py: 1,
                          px: 2,
                          minHeight: "48px",
                          ...(isSelected && {
                            bgcolor: "primary.lighter",
                            "&:hover": {
                              bgcolor: "primary.light",
                            },
                          }),
                        }}
                      >
                        {multiSelect && (
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Checkbox
                              edge="start"
                              checked={isSelected}
                              tabIndex={-1}
                              disableRipple
                              color="primary"
                              size="small"
                            />
                          </ListItemIcon>
                        )}
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            mr: 2,
                            bgcolor: isSelected ? "primary.main" : "grey.300",
                            fontSize: "0.7rem",
                          }}
                        >
                          {displayCOC.substring(0, 2).toUpperCase()}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: isSelected ? 600 : 400,
                                  color: isSelected
                                    ? "primary.main"
                                    : "text.primary",
                                }}
                              >
                                {displayCOC}
                              </Typography>
                              {sampleBottle && (
                                <Chip
                                  label={sampleBottle}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              {preservation && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Preservation: {preservation}
                                </Typography>
                              )}
                              {(jobId || jobName) && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontWeight: 500 }}
                                >
                                  Job: {jobId}{" "}
                                  {jobName && `- ${jobName}`}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < filteredSlates.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        )}

        {filteredSlates.length === 20 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block", textAlign: "center" }}
          >
            Showing first 20 results. Use search to narrow down.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "grey.50" }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        {multiSelect && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={selectedJobIds.length === 0}
            sx={{
              borderRadius: 2,
              px: 3,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
            }}
          >
            Select ({selectedJobIds.length})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SlateSelectorDialog;